import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { 
  dashboardService, 
  type DashboardMetrics, 
  type ActivityItem
} from '@/services/dashboard.service';

/**
 * Hook for fetching dashboard metrics with real-time updates
 */
export function useDashboardMetrics() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);

  const query = useQuery({
    queryKey: ['dashboard', 'metrics'],
    queryFn: () => dashboardService.getDashboardMetrics(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time feel
  });

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = dashboardService.subscribeToMetrics((newMetrics) => {
      setMetrics(newMetrics);
    });

    return unsubscribe;
  }, []);

  return {
    ...query,
    data: metrics || query.data,
  };
}

/**
 * Hook for fetching recent activity feed with real-time updates
 */
export function useRecentActivity(limit = 10) {
  const [activities, setActivities] = useState<ActivityItem[] | null>(null);

  const query = useQuery({
    queryKey: ['dashboard', 'activity', limit],
    queryFn: () => dashboardService.getRecentActivity(limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 60 * 1000, // Refetch every minute
  });

  // Subscribe to real-time activity updates
  useEffect(() => {
    const unsubscribe = dashboardService.subscribeToActivity((newActivities) => {
      setActivities(newActivities);
    }, limit);

    return unsubscribe;
  }, [limit]);

  return {
    ...query,
    data: activities || query.data,
  };
}

/**
 * Hook for fetching quick actions
 */
export function useQuickActions() {
  return useQuery({
    queryKey: ['dashboard', 'quick-actions'],
    queryFn: () => dashboardService.getQuickActions(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes
  });
}

/**
 * Hook for dashboard loading states
 */
export function useDashboardLoading() {
  const metricsQuery = useDashboardMetrics();
  const activityQuery = useRecentActivity();
  const actionsQuery = useQuickActions();

  const isLoading = metricsQuery.isLoading || activityQuery.isLoading || actionsQuery.isLoading;
  const isError = metricsQuery.isError || activityQuery.isError || actionsQuery.isError;
  const error = metricsQuery.error || activityQuery.error || actionsQuery.error;

  return {
    isLoading,
    isError,
    error,
    metrics: metricsQuery,
    activity: activityQuery,
    actions: actionsQuery,
  };
}
